import React, { useEffect, useMemo, useRef } from 'react';
import { UIGameActionButton } from '../../../../common/UIGameActionButton/index.ts';
import { UIGameResourceCardsTooltipContent } from '../../../../common/UIGameResourceCardsTooltipContent/index.ts';
import { useActionButtonCommonErrors } from '../../utils.ts';
import { T, Tooltip } from '@colonist/ui';
import { getCardsForPiece } from '@colonist/game-card-types';
import { PieceTypes } from '@colonist/game-base-types';
import IconKnightUpgrade from '@static/images/icon_knight_upgrade.svg';
import { useGameStore } from '../../../../store/GameStoreContext.ts';
import { usePlayerValidators } from '../../../../store/selectors/usePlayerValidators.ts';
import { ActionState } from '@colonist/game-states';
import { MapValidator, MechanicValidatorKnightPlayer } from '@colonist/game-mechanic-validators';
import { useCurrentStateValidator } from '../../../../store/selectors/useCurrentStateValidator.ts';
const cardsToUpgradeKnight = getCardsForPiece(PieceTypes.Knight);
export const UIGameActionButtonKnightUpgrade = ({ className, onDisabledChange })=>{
    const myColor = useGameStore((state)=>state.myColor);
    const mechanicKnightState = useGameStore((state)=>state.gameState.mechanicKnightState);
    const mapState = useGameStore((state)=>state.gameState.mapState);
    const currentStateValidator = useCurrentStateValidator();
    const { getPlayerValidator, playerValidators } = usePlayerValidators();
    const playerValidator = getPlayerValidator(myColor);
    const { errorHelperText, hasCommonErrors } = useActionButtonCommonErrors();
    const tooltipRef = useRef(null);
    const { sendUpgradeKnight, sendCancelAction, sendClearCornerHighlight } = useGameStore((state)=>state.gameSendEvent);
    const { actionStatus, knightsThatCanBeUpgradedCount } = useMemo(()=>{
        const knightPlayerState = mechanicKnightState === null || mechanicKnightState === void 0 ? void 0 : mechanicKnightState[myColor];
        if (!knightPlayerState) {
            return {
                actionStatus: {
                    canTakeAction: false
                },
                knightsThatCanBeUpgradedCount: 0
            };
        }
        const knightPlayerValidator = new MechanicValidatorKnightPlayer(playerValidator, new MapValidator(mapState), knightPlayerState);
        const knightsThatCanBeUpgradedCountLocal = knightPlayerValidator.getAmountOfKnightsEligibleForUpgrade();
        const canUpgradeKnightActionStatus = knightPlayerValidator.canPlayerUpgradeKnight();
        return {
            actionStatus: canUpgradeKnightActionStatus,
            knightsThatCanBeUpgradedCount: knightsThatCanBeUpgradedCountLocal
        };
    }, [
        playerValidators,
        mapState,
        mechanicKnightState,
        myColor
    ]);
    const triggerFlickerCardsAnimation = ()=>{
        const notEnoughResources = !playerValidator.hasResourceCardsInHand(cardsToUpgradeKnight);
        if (notEnoughResources) {
            var _tooltipRef_current;
            (_tooltipRef_current = tooltipRef.current) === null || _tooltipRef_current === void 0 ? void 0 : _tooltipRef_current.triggerFlickerAnimation();
        }
    };
    const disabled = hasCommonErrors || !actionStatus.canTakeAction;
    const actionState = currentStateValidator.getPlayerActionState(myColor);
    const isUpgradingKnight = actionState === ActionState.UpgradeKnight && !disabled;
    const helperText = errorHelperText || actionStatus.errorMessageIfCantTakeAction;
    useEffect(()=>{
        onDisabledChange(disabled);
    }, [
        disabled
    ]);
    const onClick = ()=>{
        if (disabled) return;
        if (isUpgradingKnight) {
            sendClearCornerHighlight();
            sendCancelAction();
        } else {
            sendUpgradeKnight();
        }
    };
    return /*#__PURE__*/ React.createElement(Tooltip, {
        position: "bottom",
        title: /*#__PURE__*/ React.createElement(T, {
            id: "strings:game.tooltips.upgradeKnight"
        })
    }, /*#__PURE__*/ React.createElement(UIGameActionButton, {
        className: className,
        count: knightsThatCanBeUpgradedCount,
        countColor: myColor,
        disabled: disabled,
        helperText: helperText,
        highlight: isUpgradingKnight,
        iconPath: IconKnightUpgrade,
        onClickActionIfDisabled: triggerFlickerCardsAnimation,
        tooltipCustomMenuComponent: /*#__PURE__*/ React.createElement(UIGameResourceCardsTooltipContent, {
            cardInventory: playerValidator.resourceCards.cards,
            cardsToBuild: cardsToUpgradeKnight,
            ref: tooltipRef
        }),
        onClick: onClick
    }));
};
